import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Info, ListChecks } from "lucide-react"
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { Link, useInRouterContext, useLocation } from "react-router"
import { SETTINGS_PATHS } from "../paths"
import {
  openedFromSetup,
  SETTINGS_OUTPUT_LABEL_KEYS,
  settingsTarget,
  type SettingsOutput,
  type SettingsTargetId,
} from "../settingsTargets"
import { InfoTip } from "./InfoTip"

/** Title and description with an action to their right; shared by the page
 *  and its sections so both line up the same way. */
function Heading({
  title,
  description,
  note,
  action,
}: {
  title: ReactNode
  description?: string
  /** Below the description, e.g. where the setting is used. */
  note?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title}
        {description && <Typography as="muted">{description}</Typography>}
        {note}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

interface SettingsPageProps {
  title: string
  description?: string
  /** `wide` for pages with live previews beside their forms. */
  width?: "default" | "wide"
  /** A short note above the sections, e.g. who a change applies to. */
  notice?: string
  /** Right of the title: the page's main action, e.g. "Add signature". */
  action?: ReactNode
  children: ReactNode
}

/** The frame every settings page uses: title, description, an optional
 *  notice, then its sections separated by rules. */
export function SettingsPage({
  title,
  description,
  width = "default",
  notice,
  action,
  children,
}: SettingsPageProps) {
  return (
    <div
      className={cn(
        "mx-auto flex w-full flex-col gap-6",
        width === "wide" ? "max-w-5xl" : "max-w-3xl",
      )}
    >
      <Heading
        title={
          <Typography as="h3" role="heading" aria-level={1}>
            {title}
          </Typography>
        }
        description={description}
        action={action}
      />
      {notice && (
        // A standing note, not an alert: nothing to announce on every visit.
        <Alert role="note" className="border-0 bg-accent">
          <Info className="size-4" />
          <Typography as="muted">{notice}</Typography>
        </Alert>
      )}
      <div className="flex flex-col divide-y">{children}</div>
    </div>
  )
}

interface SettingsSectionProps {
  title?: string
  /** Tooltip text behind an (i) button next to the title. */
  info?: string
  description?: string
  /** Right of the section title, e.g. "New template". */
  action?: ReactNode
  /** Below the content: the section's Save button, and any status next to it. */
  footer?: ReactNode
  /** The saved value is still empty or a placeholder: marked next to the
   *  title, like the page in the nav and its item on the setup checklist. */
  needsSetup?: boolean
  /** The setting this section edits (see settingsTargets): where its value
   *  is used, under the description, and the anchor a link's `?focus=`
   *  scrolls to. */
  target?: SettingsTargetId
  children?: ReactNode
}

/** "Used in: Emails to suppliers · …", every output the value reaches. */
function UsedInLine({ outputs }: { outputs: readonly SettingsOutput[] }) {
  const { translate } = useTranslation()
  return (
    <Typography as="muted" className="text-xs">
      {translate("SETTINGS/USED_IN/LABEL", {
        outputs: outputs
          .map((output) => translate(SETTINGS_OUTPUT_LABEL_KEYS[output]))
          .join(" · "),
      })}
    </Typography>
  )
}

/** How long a landing keeps the section in place. The pulse around it (in
 *  index.css) is over well before. */
const ARRIVAL_MS = 2000

/** Brings the section into view, with one short pulse around it, when the
 *  URL's `?focus=` names it: where a link to the setting lands. */
function FocusOnArrival({
  focus,
  sectionRef,
  onHighlight,
}: {
  focus: string
  sectionRef: RefObject<HTMLElement | null>
  onHighlight: (highlighted: boolean) => void
}) {
  const { search, key } = useLocation()
  const wanted = new URLSearchParams(search).get("focus") === focus

  // After the settings app's own scroll reset on a page change (a layout
  // effect), so it is not undone. Again for every link followed.
  useEffect(() => {
    const section = sectionRef.current
    if (!wanted || !section) return
    section.scrollIntoView?.({ block: "start", behavior: "smooth" })
    onHighlight(true)
    // Sections above may still be loading their data and push this one down
    // as they render: keep it in place meanwhile.
    const content = scrollParent(section)?.firstElementChild
    const observer =
      content && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => section.scrollIntoView?.({ block: "start" }))
        : null
    if (content) observer?.observe(content)
    const timer = setTimeout(() => {
      onHighlight(false)
      observer?.disconnect()
    }, ARRIVAL_MS)
    return () => {
      clearTimeout(timer)
      observer?.disconnect()
    }
  }, [wanted, key, sectionRef, onHighlight])
  return null
}

/** The nearest ancestor that scrolls: the settings page column. */
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === "auto" || overflowY === "scroll") return parent
  }
  return null
}

/** Below a section's content: its Save button and any status next to it. */
function SectionFooter({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>
}

/** The footer of a section a link can land on. Opened from the setup
 *  checklist, it also leads back there, after the Save button, for the next
 *  item; for anyone who came another way it is the plain footer. */
function TargetFooter({
  focus,
  children,
}: {
  focus: string
  children?: ReactNode
}) {
  const { translate } = useTranslation()
  const fromSetup = openedFromSetup(useLocation().search, focus)
  if (!children && !fromSetup) return null
  return (
    <SectionFooter>
      {children}
      {fromSetup && (
        <Button variant="outline" asChild>
          <Link to={SETTINGS_PATHS.setup} replace>
            <ListChecks aria-hidden />
            {translate("SETTINGS/SETUP/CONTINUE")}
          </Link>
        </Button>
      )}
    </SectionFooter>
  )
}

/** Next to a section title whose setting is still empty or a placeholder.
 *  Placed as an item of the title's flex row, so it centres on the title; in
 *  a wrapper it sits on the wrapper's text baseline instead, 2px too low. */
export function NeedsSetupBadge({ className }: { className?: string }) {
  const { translate } = useTranslation()
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-warning" />
      {translate("SETTINGS/SETUP/NEEDS_SETUP")}
    </span>
  )
}

export function SettingsSection({
  title,
  info,
  description,
  action,
  footer,
  needsSetup = false,
  target,
  children,
}: SettingsSectionProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const [highlighted, setHighlighted] = useState(false)
  // Pages are also rendered on their own, outside a router (their tests).
  const inRouter = useInRouterContext()
  const def = target ? settingsTarget(target) : undefined

  return (
    <section
      ref={sectionRef}
      id={def ? `settings-section-${def.focus}` : undefined}
      // The scroll margin keeps the pulse's top edge in view on arrival.
      className={cn(
        "flex scroll-mt-6 flex-col gap-4 rounded-md py-6 first:pt-0 last:pb-0",
        highlighted && "animate-focus-pulse motion-reduce:animate-none",
      )}
    >
      {def && inRouter && (
        <FocusOnArrival
          focus={def.focus}
          sectionRef={sectionRef}
          onHighlight={setHighlighted}
        />
      )}
      {(title || description || action) && (
        <Heading
          title={
            title && (
              <div className="flex flex-wrap items-center gap-1">
                <Typography as="large" role="heading" aria-level={2}>
                  {title}
                </Typography>
                {info && <InfoTip text={info} />}
                {needsSetup && <NeedsSetupBadge className="ml-1" />}
              </div>
            )
          }
          description={description}
          note={def?.usedIn && <UsedInLine outputs={def.usedIn} />}
          action={action}
        />
      )}
      {children}
      {def && inRouter ? (
        <TargetFooter focus={def.focus}>{footer}</TargetFooter>
      ) : (
        footer && <SectionFooter>{footer}</SectionFooter>
      )}
    </section>
  )
}

interface SettingsFieldProps {
  /** The control's id, so the label focuses it. */
  htmlFor?: string
  label: string
  /** Tooltip text behind an (i) button next to the label. */
  info?: string
  /** Help text below the control. */
  hint?: string
  children: ReactNode
}

export function SettingsField({
  htmlFor,
  label,
  info,
  hint,
  children,
}: SettingsFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={htmlFor}>{label}</Label>
        {info && <InfoTip text={info} />}
      </div>
      {children}
      {hint && <Typography as="muted">{hint}</Typography>}
    </div>
  )
}
